import '../styles/home.css'
import React from 'react'
import { useLocation, Link } from 'react-router-dom'

import LandingGlobalHeader from '../components/landing/LandingGlobalHeader'
import LandingFooter from '../components/landing/LandingFooter'
import useLandingAuth from '../components/landing/useLandingAuth'

const METHODOLOGY_PDF_URL = 'https://d3h48y1385wws8.cloudfront.net/methodology/LMW-Technical%20Documentation.pdf'
const METHODOLOGY_PDF_EMBED_URL = `${METHODOLOGY_PDF_URL}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`

const PAGE_CONTENT = {
  '/data-center': {
    eyebrow: 'Data Center',
    title: 'A dedicated download workspace is in progress.',
    description: 'We are shaping a standalone data center experience for more structured extraction flows, including download presets, request templates, and clearer dataset pathways.',
    bullets: [
      'Indicator-first and country-first request builders',
      'More explicit download presets for common workflows',
      'A tighter bridge between search results and exports',
    ],
  },
  '/country-briefings': {
    eyebrow: 'Country Briefings',
    title: 'Country briefings and editorial notes are being prepared.',
    description: 'This page will host country-specific briefings, regional summaries, and short-form analytical notes designed to sit on top of the LMW data infrastructure.',
    bullets: [
      'Country-specific briefings and recurring updates',
      'Short editorial notes linked to the underlying data',
      'A cleaner publishing surface for periodic publications',
    ],
  },
  '/methodology': {
    eyebrow: 'Methodology',
    title: 'Technical documentation is being organized.',
    description: 'We are building a clearer methodology section to document series definitions, coding structures, transformations, and source traceability in one place.',
    bullets: [
      'Series definitions and construction logic',
      'Source traceability and update conventions',
      'Technical notes for units, frequencies, and transformations',
    ],
  },
}

function MarketingPlaceholderScreen() {
  const location = useLocation()
  const { loadingUser, userInfo, openModal, logoutHandler, authModals } = useLandingAuth()
  const page = PAGE_CONTENT[location.pathname] || PAGE_CONTENT['/country-briefings']
  const isMethodologyPage = location.pathname === '/methodology'

  return (
    <div className='landing-page-shell landing-secondary-page'>
      <LandingGlobalHeader
        loadingUser={loadingUser}
        userInfo={userInfo}
        onOpenModal={openModal}
        onLogout={logoutHandler}
      />

      <main>
        {isMethodologyPage ? (
          <section className='landing-methodology-section'>
            <div className='container landing-methodology-shell'>
              <div className='landing-methodology-intro'>
                <span className='landing-section-kicker'>Methodology</span>
                <h1>LMW Technical Documentation</h1>
                <p>The documentation is presented as a reading surface inside the page, with direct access to the original PDF for full-screen review or download.</p>
              </div>

              <div className='landing-book-stage'>
                <div className='landing-book-viewer'>
                  <div className='landing-book-spine'></div>
                  <div className='landing-book-cover-shadow'></div>
                  <div className='landing-book-pages landing-book-left-page'>
                    <div className='landing-book-page-content'>
                      <span className='landing-book-sidebar-label'>Document</span>
                      <h2>Technical Documentation</h2>
                      <p>
                        A reference manual for indicator definitions, source traceability, units, transformations, update conventions, and compilation standards used across the Latin Macro Watch dataset.
                      </p>

                      <div className='landing-book-meta'>
                        <div>
                          <strong>Format</strong>
                          <span>PDF</span>
                        </div>
                        <div>
                          <strong>Experience</strong>
                          <span>Inline reading</span>
                        </div>
                        <div>
                          <strong>Scope</strong>
                          <span>Methodology</span>
                        </div>
                        <div>
                          <strong>Source</strong>
                          <span>IDB / LMW</span>
                        </div>
                      </div>

                      <ul className='landing-book-highlights'>
                        <li>Indicator construction and coding logic</li>
                        <li>Frequency and transformation conventions</li>
                        <li>Source handling and update standards</li>
                      </ul>

                      <div className='landing-placeholder-actions landing-book-actions'>
                        <a className='landing-hero-primary compact' href={METHODOLOGY_PDF_URL} target='_blank' rel='noreferrer'>Open Full PDF</a>
                        <Link className='landing-hero-secondary compact landing-book-secondary' to='/search'>Open Search</Link>
                      </div>
                    </div>
                  </div>

                  <div className='landing-book-pages landing-book-right-page'>
                    <iframe
                      className='landing-book-iframe'
                      src={METHODOLOGY_PDF_EMBED_URL}
                      title='LMW Technical Documentation'
                    />
                  </div>
                </div>
              </div>

              <div className='landing-methodology-next-step'>
                <div className='landing-methodology-next-step-card'>
                  <span className='landing-section-kicker'>Need a faster overview?</span>
                  <h2>Go to the FAQ version.</h2>
                  <p>
                    If you want a shorter, question-based summary of the methodology and dataset logic, the FAQ page provides a more direct reading path.
                  </p>
                  <div className='landing-placeholder-actions landing-methodology-next-actions'>
                    <Link className='landing-hero-primary compact landing-methodology-faq-button' to='/faq'>Open FAQ</Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        ) : (
          <>
            <section className='landing-page-hero landing-page-hero-placeholder'>
              <div className='container landing-page-hero-inner narrow'>
                <span className='landing-page-eyebrow'>{page.eyebrow}</span>
                <h1>{page.title}</h1>
                <div className='landing-page-hero-bar'></div>
                <p>{page.description}</p>
              </div>
            </section>

          <section className='landing-placeholder-section'>
            <div className='container landing-placeholder-grid'>
              <article className='landing-placeholder-card'>
                <span className='landing-section-kicker'>Working on it</span>
                <h2>What will be here</h2>
                <ul className='landing-placeholder-list'>
                  {page.bullets.map(item => <li key={item}>{item}</li>)}
                </ul>
              </article>

              <article className='landing-placeholder-card accent'>
                <span className='landing-section-kicker'>Available today</span>
                <h2>Use Search in the meantime</h2>
                <p>
                  The search workspace is already live and remains the main operational interface for data exploration, charting, and export-ready configuration.
                </p>
                <div className='landing-placeholder-actions'>
                  <Link className='landing-hero-primary compact' to='/search'>Open Search</Link>
                  <Link className='landing-hero-secondary compact placeholder-secondary' to='/'>Back to Home</Link>
                </div>
              </article>
            </div>
          </section>
          </>
        )}
      </main>

      <LandingFooter />
      {authModals}
    </div>
  )
}

export default MarketingPlaceholderScreen
